import { c as _c } from "react/compiler-runtime";
import { INITIAL_APP_URL, INITIAL_REFERRER, LOCALE } from '@/constants/config';
import useAvailabilityFiltersHandlers from '@/contexts/availabilityFiltersContext/hooks/useAvailabilityFiltersHandlers';
import useCurrency from '@/contexts/i18nContext/hooks/useCurrency';
import useTranslate from '@/contexts/i18nContext/hooks/useTranslate';
import usePageExperienceId from '@/contexts/pageContext/hooks/usePageExperienceId';
import usePageFormErrors from '@/contexts/pageContext/hooks/usePageFormErrors';
import usePageName from '@/contexts/pageContext/hooks/usePageName';
import usePageTunnelBookingType from '@/contexts/pageContext/hooks/usePageTunnelBookingType';
import usePageTunnelStep from '@/contexts/pageContext/hooks/usePageTunnelStep';
import useFieldError from '@/hooks/useFieldError';
import useQuery from '@/hooks/useQuery';
import { BookingSubmitMutation, CalculatedPriceQuery, CheckBookingStatusQuery } from '@/queries/cart.gql';
import { BBookingPaymentStatusEnum } from '@/types/backend';
import { FFormIdEnum } from '@/types/form';
import { FPageNameEnum } from '@/types/pages';
import { BPaymentBehaviourEnum } from '@/types/temp';
import { FTunnelPageStepEnum } from '@/types/tunnel';
import { FValidationErrorTypeEnum } from '@/types/validation';
import pushGTMDataLayer from '@/utils/gtm/pushGTMDataLayer';
import sendGTMConfirmBookingFailed from '@/utils/gtm/sendGTMConfirmBookingFailed';
import buildTunnelHref from '@/utils/routes/buildTunnelHref';
import safeLog, { safeWarn } from '@/utils/safeLog';
import { useApolloClient, useMutation } from '@apollo/client';
import { captureException, setExtra } from '@sentry/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { capitalize, formatPrice } from '@winelidays/wld-tsx-2/utils';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import useStripeSetAmount from '../stripeContext/hooks/useStripeSetAmount';
import CartContext from './cart.context';
export const CartProvider = (t0) => {
    const $ = _c(182);
    const { children } = t0;
    const translate = useTranslate();
    const { symbol: currencySymbol } = useCurrency();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const { handleSaveFilters } = useAvailabilityFiltersHandlers();
    const experienceId = usePageExperienceId();
    const pageName = usePageName();
    const isInTunnel = pageName === FPageNameEnum.tunnel;
    const tunnelStep = usePageTunnelStep();
    const bookingType = usePageTunnelBookingType();
    let t1;
    if ($[0] !== bookingType || $[1] !== experienceId) {
        t1 = bookingType && experienceId ? buildTunnelHref(bookingType, experienceId) : "/";
        $[0] = bookingType;
        $[1] = experienceId;
        $[2] = t1;
    }
    else {
        t1 = $[2];
    }
    const bookingPath = t1;
    const isOnValidationPage = isInTunnel && tunnelStep === FTunnelPageStepEnum.validation;
    let t2;
    if ($[3] !== bookingType || $[4] !== experienceId) {
        t2 = bookingType && experienceId ?
            buildTunnelHref(bookingType, experienceId, FTunnelPageStepEnum.validation) :
            "/";
        $[3] = bookingType;
        $[4] = experienceId;
        $[5] = t2;
    }
    else {
        t2 = $[5];
    }
    const validationPath = t2;
    const isOnConfirmationPage = isInTunnel && tunnelStep === FTunnelPageStepEnum.confirmation;
    let t3;
    if ($[6] !== bookingType || $[7] !== experienceId) {
        t3 = bookingType && experienceId ?
            buildTunnelHref(bookingType, experienceId, FTunnelPageStepEnum.confirmation) :
            "/";
        $[6] = bookingType;
        $[7] = experienceId;
        $[8] = t3;
    }
    else {
        t3 = $[8];
    }
    const confirmationPath = t3;
    const [reservationInProgress, setReservationInProgress] = useState(null);
    const [specialRequest, setSpecialRequest] = useState("");
    const [initialCalculatedPrice, setInitialCalculatedPrice] = useState(null);
    const [discountCalculatedPrice, setDiscountCalculatedPrice] = useState(null);
    const calculatedPrice = discountCalculatedPrice ?? initialCalculatedPrice;
    const totalPrice = calculatedPrice?.total ?? null;
    const conversionAmount = calculatedPrice?.feesBeforeTax ?? null;
    const totalPriceBeforeDiscount = calculatedPrice?.totalPriceBeforeDiscount ?? null;
    const paymentBehaviour = totalPrice ? BPaymentBehaviourEnum.Checkout : BPaymentBehaviourEnum.Free;
    const [discountCodeError, setDiscountCodeError] = useState(false);
    const [discountCode, setDiscountCode] = useState(null);
    let t4;
    if ($[9] !== discountCalculatedPrice || $[10] !== discountCode) {
        t4 = discountCalculatedPrice?.discountAmount && discountCode ? { discountUnusedAmount: discountCalculatedPrice.discountUnusedAmount ?? 0, totalDiscount: discountCalculatedPrice.discountAmount, totalPriceBeforeDiscount: discountCalculatedPrice.totalPriceBeforeDiscount, discountCode } : null;
        $[9] = discountCalculatedPrice;
        $[10] = discountCode;
        $[11] = t4;
    }
    else {
        t4 = $[11];
    }
    const discount = t4;
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = () => {
            setDiscountCodeError(false);
        };
        $[12] = t5;
    }
    else {
        t5 = $[12];
    }
    const resetDiscountCodeError = t5;
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = (newCode) => {
            resetDiscountCodeError();
            setDiscountCode(newCode ?? null);
            setDiscountCalculatedPrice(null);
        };
        $[13] = t6;
    }
    else {
        t6 = $[13];
    }
    const resetDiscount = t6;
    const setStripeAmount = useStripeSetAmount();
    let t7;
    let t8;
    if ($[14] !== setStripeAmount || $[15] !== totalPrice) {
        t7 = () => {
            setStripeAmount(totalPrice ? totalPrice : 1);
        };
        t8 = [setStripeAmount, totalPrice];
        $[14] = setStripeAmount;
        $[15] = totalPrice;
        $[16] = t7;
        $[17] = t8;
    }
    else {
        t7 = $[16];
        t8 = $[17];
    }
    useEffect(t7, t8);
    const [firstName, setFirstName] = useState("");
    const [firstNameError, setIsFirstNameInError, setFirstNameError] = useFieldError();
    const [lastName, setLastName] = useState("");
    const [lastNameError, setIsLastNameInError, setLastNameError] = useFieldError();
    const [email, setEmail] = useState("");
    const [emailError, setIsEmailInError, setEmailError] = useFieldError();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneCountry, setPhoneCountry] = useState(null);
    const phone = phoneCountry?.countryCode && phoneNumber ?
        `${phoneCountry.countryCode.toUpperCase()}+${phoneCountry.dialCode} ${phoneNumber}` :
        "";
    const [phoneError, setIsPhoneInError, setPhoneError] = useFieldError();
    const [optInEstablishments, setOptInEstablishments] = useState(false);
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = () => {
            setFirstName("");
            setLastName("");
            setEmail("");
            setPhoneNumber("");
            setPhoneCountry(null);
        };
        $[18] = t9;
    }
    else {
        t9 = $[18];
    }
    const resetContactForm = t9;
    let t10;
    if ($[19] !== email || $[20] !== firstName || $[21] !== lastName || $[22] !== optInEstablishments || $[23] !== phone) {
        t10 = { firstName, lastName, email, phone, optInEstablishments };
        $[19] = email;
        $[20] = firstName;
        $[21] = lastName;
        $[22] = optInEstablishments;
        $[23] = phone;
        $[24] = t10;
    }
    else {
        t10 = $[24];
    }
    const user = t10;
    const [isContactFormDisabled, setIsContactFormDisabled] = useState(false);
    const [submittingBooking, setSubmittingBooking] = useState(false);
    const [checkingBookingStatus, setCheckingBookingStatus] = useState(false);
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = v4();
        $[25] = t11;
    }
    else {
        t11 = $[25];
    }
    const [token, setToken] = useState(t11);
    let t12;
    let t13;
    if ($[26] !== token) {
        t12 = () => {
            pushGTMDataLayer({ booking_token: token });
        };
        t13 = [token];
        $[26] = token;
        $[27] = t12;
        $[28] = t13;
    }
    else {
        t12 = $[27];
        t13 = $[28];
    }
    useEffect(t12, t13);
    const [usedParams, setUsedParams] = useState(null);
    const billingName = usedParams?.user.firstName != null &&
        usedParams?.user.firstName !== "" &&
        usedParams?.user.lastName != null &&
        usedParams?.user.lastName !== "" ?
        `${usedParams.user.firstName} ${usedParams.user.lastName}` : null;
    const [bookingId, setBookingId] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    let t14;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = () => {
            setReservationInProgress(null);
            setIsContactFormDisabled(false);
            setUsedParams(null);
            setToken(v4());
            setBookingId(null);
            setClientSecret(null);
            pushGTMDataLayer({ transaction_id: undefined, items: undefined, currency: undefined, value: undefined, has_online_payment: undefined, tunnel_mode: undefined, total_price: undefined, not_refundable: undefined });
        };
        $[29] = t14;
    }
    else {
        t14 = $[29];
    }
    const handleResetValidation = t14;
    let t15;
    if ($[30] !== discountCalculatedPrice?.discountAmount || $[31] !== discountCode || $[32] !== reservationInProgress || $[33] !== specialRequest || $[34] !== user) {
        t15 = reservationInProgress != null ? { ...reservationInProgress, discountCode: discountCalculatedPrice?.discountAmount ? discountCode : null, locale: LOCALE, specialRequest, user } : null;
        $[30] = discountCalculatedPrice?.discountAmount;
        $[31] = discountCode;
        $[32] = reservationInProgress;
        $[33] = specialRequest;
        $[34] = user;
        $[35] = t15;
    }
    else {
        t15 = $[35];
    }
    const params = t15;
    const stripe = useStripe();
    const elements = useElements();
    const [creditCardError, setIsCreditCardInError, setCreditCardError] = useFieldError();
    const { setShowFormErrors } = usePageFormErrors(FFormIdEnum.CART_VALIDATION);
    let t16;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = () => {
            setInitialCalculatedPrice(null);
            setDiscountCalculatedPrice(null);
        };
        $[36] = t16;
    }
    else {
        t16 = $[36];
    }
    const resetCalculatedPrice = t16;
    let t17;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = () => {
            setReservationInProgress(null);
            setSpecialRequest("");
            resetDiscount();
            setInitialCalculatedPrice(null);
        };
        $[37] = t17;
    }
    else {
        t17 = $[37];
    }
    const resetCart = t17;
    const [enRouteToConfirmation, setEnRouteToConfirmation] = useState(false);
    let t18;
    if ($[38] !== confirmationPath || $[39] !== navigate) {
        t18 = () => {
            setEnRouteToConfirmation(true);
            navigate(confirmationPath);
        };
        $[38] = confirmationPath;
        $[39] = navigate;
        $[40] = t18;
    }
    else {
        t18 = $[40];
    }
    const handleValidationSuccess = t18;
    const t19 = reservationInProgress?.bookableId;
    const t20 = reservationInProgress?.remainingStock;
    const t21 = reservationInProgress?.extras;
    const t22 = reservationInProgress?.dateTime;
    let t23;
    if ($[41] !== discountCode || $[42] !== t19 || $[43] !== t20 || $[44] !== t21 || $[45] !== t22) {
        t23 = { bookableId: t19, discountCode, remainingStock: t20, extras: t21, experienceDate: t22, locale: LOCALE };
        $[41] = discountCode;
        $[42] = t19;
        $[43] = t20;
        $[44] = t21;
        $[45] = t22;
        $[46] = t23;
    }
    else {
        t23 = $[46];
    }
    const t24 = !reservationInProgress || !discountCode && !!initialCalculatedPrice;
    let t25;
    if ($[47] !== discountCode || $[48] !== t23 || $[49] !== t24) {
        t25 = { fetchPolicy: "cache-and-network", variables: t23, skip: t24, onCompleted(data) {
                if (discountCode) {
                    if (data.calculatedPrice.discountAmount) {
                        setDiscountCalculatedPrice(data.calculatedPrice);
                    }
                    else {
                        setDiscountCodeError(true);
                    }
                }
                else {
                    setInitialCalculatedPrice(data.calculatedPrice);
                }
            }, onError(error) {
                alert(error);
            } };
        $[47] = discountCode;
        $[48] = t23;
        $[49] = t24;
        $[50] = t25;
    }
    else {
        t25 = $[50];
    }
    const { loading: loadingCalculatedPrice } = useQuery(CalculatedPriceQuery, t25);
    let t26;
    if ($[51] !== bookingPath || $[52] !== bookingType || $[53] !== navigate || $[54] !== translate) {
        t26 = (reason) => {
            safeWarn("handleValidationFailed", reason);
            sendGTMConfirmBookingFailed(reason, bookingType);
            if (reason.message === BBookingPaymentStatusEnum.StockError ||
                reason.message.includes(FValidationErrorTypeEnum.SLOT_IS_FULL_PARTIAL_ERROR_MSG)) {
                const messages = [translate("validation_page.slot_full_error.content")];
                if (reason.message === BBookingPaymentStatusEnum.StockError) {
                    messages.push(translate("validation_page.error_after_payment.content"));
                }
                else {
                    messages.push(translate("validation_page.error_before_payment.content"));
                }
                alert(`${translate("validation_page.slot_full_error.title")}\n\n${messages.join("\n\n")}`);
                navigate(bookingPath);
            }
            else {
                if (!reason.message.includes(FValidationErrorTypeEnum.KNOWN_STRIPE_ERROR_MSG)) {
                    const messages_0 = [translate("validation_page.standard_error.content")];
                    if (reason.message === BBookingPaymentStatusEnum.PaymentError || !Object.values(BBookingPaymentStatusEnum).includes(reason.message)) {
                        messages_0.push(translate("validation_page.error_before_payment.content"));
                    }
                    else {
                        messages_0.push(translate("validation_page.error_after_payment.content"));
                    }
                    alert(`${translate("validation_page.standard_error.title")}\n\n${messages_0.join("\n\n")}`);
                    if (reason.message.includes(FValidationErrorTypeEnum.UNKNOWN_FORM_ERROR_MSG)) {
                        navigate(bookingPath);
                    }
                }
            }
        };
        $[51] = bookingPath;
        $[52] = bookingType;
        $[53] = navigate;
        $[54] = translate;
        $[55] = t26;
    }
    else {
        t26 = $[55];
    }
    const handleValidationFailed = t26;
    let t27;
    if ($[56] !== apolloClient || $[57] !== handleValidationFailed || $[58] !== handleValidationSuccess) {
        t27 = async (token_0) => {
            let keepPolling = true;
            const poll = () => {
                setTimeout(() => {
                    apolloClient.query({ query: CheckBookingStatusQuery, variables: { token: token_0 } }).then((res) => {
                        if (res.data.checkBookingStatus.status !== BBookingPaymentStatusEnum.Processing) {
                            setCheckingBookingStatus(false);
                            bb226: switch (res.data.checkBookingStatus.status) {
                                case BBookingPaymentStatusEnum.Success: {
                                    handleValidationSuccess();
                                    break bb226;
                                }
                                case BBookingPaymentStatusEnum.InternalServerError: { }
                                case BBookingPaymentStatusEnum.PaymentError: { }
                                case BBookingPaymentStatusEnum.StockError: { }
                                default: {
                                    handleValidationFailed(new Error(res.data.checkBookingStatus.status));
                                }
                            }
                            keepPolling = false;
                        }
                        else {
                            safeLog("Processing booking...");
                        }
                    }).finally(() => {
                        setCheckingBookingStatus((c) => {
                            if (c && keepPolling) {
                                poll();
                            }
                            if (!keepPolling) {
                                return false;
                            }
                            return (c);
                        });
                    });
                }, 3000);
            };
            poll();
        };
        $[56] = apolloClient;
        $[57] = handleValidationFailed;
        $[58] = handleValidationSuccess;
        $[59] = t27;
    }
    else {
        t27 = $[59];
    }
    const handleCheckBookingStatus = t27;
    let t28;
    if ($[60] !== billingName || $[61] !== bookingType || $[62] !== elements || $[63] !== experienceId || $[64] !== handleCheckBookingStatus || $[65] !== setCreditCardError || $[66] !== setShowFormErrors || $[67] !== stripe || $[68] !== token) {
        t28 = (clientSecret_0) => {
            setExtra("validation.clientSecret", clientSecret_0);
            if (!stripe || !elements) {
                safeWarn("StripeConfirm no stripe or elements!", stripe, elements);
                setExtra("validation.stripe.stripe", JSON.stringify(stripe));
                setExtra("validation.stripe.elements", JSON.stringify(elements));
                sendGTMConfirmBookingFailed(new Error(`handleStripeConfirm failed: no ${stripe ? "elements" : "stripe"} object`), bookingType);
                captureException(new Error("handleStripeConfirm no stripe or elements"));
            }
            else {
                if (!token) {
                    safeWarn("StripeConfirm no token!");
                    setExtra("validation.token", token);
                    sendGTMConfirmBookingFailed(new Error("handleStripeConfirm failed: no booking_token"), bookingType);
                    captureException(new Error("handleStripeConfirm failed: no token"));
                }
                else {
                    elements.submit().then((submitRes) => {
                        if (submitRes.error) {
                            if (submitRes.error?.message != null) {
                                setShowFormErrors(true);
                                setCreditCardError(submitRes.error.message);
                                throw new Error(`stripe elements.submit failed: ${FValidationErrorTypeEnum.KNOWN_STRIPE_ERROR_MSG} ${submitRes.error.message}`, { cause: submitRes.error });
                            }
                            else {
                                throw new Error(`stripe elements.submit failed: ${FValidationErrorTypeEnum.UNKNOWN_STRIPE_ERROR_MSG}`, { cause: submitRes.error });
                            }
                        }
                        setCheckingBookingStatus(true);
                        handleCheckBookingStatus(token);
                        return (stripe.confirmPayment({ elements, clientSecret: clientSecret_0, confirmParams: { return_url: `${INITIAL_APP_URL}&confirmExperienceId=${experienceId}&redirectUrl=${encodeURIComponent(INITIAL_REFERRER ?? "")}`, payment_method_data: { billing_details: { name: billingName } } } }).then((confirmRes) => {
                            setCheckingBookingStatus(false);
                            if (confirmRes.error?.message != null) {
                                setShowFormErrors(true);
                                setCreditCardError(confirmRes.error.message);
                                throw new Error(`stripe.confirmPayment failed: ${FValidationErrorTypeEnum.KNOWN_STRIPE_ERROR_MSG} ${confirmRes.error.message}`, { cause: confirmRes.error });
                            }
                            else {
                                throw new Error(`stripe.confirmPayment failed: ${FValidationErrorTypeEnum.UNKNOWN_STRIPE_ERROR_MSG}`, { cause: confirmRes.error });
                            }
                        }));
                    }).catch((err) => {
                        sendGTMConfirmBookingFailed(err, bookingType);
                        safeWarn("stripe confirmPayment failed", err);
                    }).finally(() => {
                        setSubmittingBooking(false);
                    });
                }
            }
        };
        $[60] = billingName;
        $[61] = bookingType;
        $[62] = elements;
        $[63] = experienceId;
        $[64] = handleCheckBookingStatus;
        $[65] = setCreditCardError;
        $[66] = setShowFormErrors;
        $[67] = stripe;
        $[68] = token;
        $[69] = t28;
    }
    else {
        t28 = $[69];
    }
    const handleStripeConfirm = t28;
    let t29;
    if ($[70] !== bookingType || $[71] !== handleValidationFailed || $[72] !== setEmailError || $[73] !== setFirstNameError || $[74] !== setLastNameError || $[75] !== setPhoneError || $[76] !== setShowFormErrors || $[77] !== translate) {
        t29 = (formValidation) => {
            const formErrors = [];
            if (formValidation.email.errors &&
                formValidation.email.errors.length &&
                formValidation.email.errors[0].parameters) {
                const params_0 = Object.fromEntries(formValidation.email.errors[0].parameters.map(_temp));
                formErrors.push(`email error: ${formValidation.email.errors[0].message}`);
                setEmailError(translate(`form.error.${formValidation.email.errors[0].message}`, params_0));
            }
            if (formValidation.firstName.errors &&
                formValidation.firstName.errors.length &&
                formValidation.firstName.errors[0].parameters) {
                const params_1 = Object.fromEntries(formValidation.firstName.errors[0].parameters.map(_temp2));
                formErrors.push(`firstName error: ${formValidation.firstName.errors[0].message}`);
                formErrors.push(formValidation.firstName.errors[0].message);
                setFirstNameError(translate(`form.error.${formValidation.firstName.errors[0].message}`, params_1));
            }
            if (formValidation.lastName.errors &&
                formValidation.lastName.errors.length &&
                formValidation.lastName.errors[0].parameters) {
                const params_2 = Object.fromEntries(formValidation.lastName.errors[0].parameters.map(_temp3));
                formErrors.push(`lastName error: ${formValidation.lastName.errors[0].message}`);
                setLastNameError(translate(`form.error.${formValidation.lastName.errors[0].message}`, params_2));
            }
            if (formValidation.phone.errors &&
                formValidation.phone.errors.length &&
                formValidation.phone.errors[0].parameters) {
                const params_3 = Object.fromEntries(formValidation.phone.errors[0].parameters.map(_temp4));
                formErrors.push(`phone error: ${formValidation.phone.errors[0].message}`);
                setPhoneError(translate(`form.error.${formValidation.phone.errors[0].message}`, params_3));
            }
            if (formErrors.length) {
                sendGTMConfirmBookingFailed(new Error(FValidationErrorTypeEnum.INVALID_FORM_ERROR_MSG, { cause: formErrors }), bookingType);
                setShowFormErrors(true);
            }
            else {
                handleValidationFailed(new Error(FValidationErrorTypeEnum.UNKNOWN_FORM_ERROR_MSG, { cause: formValidation }));
            }
        };
        $[70] = bookingType;
        $[71] = handleValidationFailed;
        $[72] = setEmailError;
        $[73] = setFirstNameError;
        $[74] = setLastNameError;
        $[75] = setPhoneError;
        $[76] = setShowFormErrors;
        $[77] = translate;
        $[78] = t29;
    }
    else {
        t29 = $[78];
    }
    const handleSubmittedContactFormErrors = t29;
    let t30;
    if ($[79] !== bookingPath || $[80] !== handleStripeConfirm || $[81] !== handleSubmittedContactFormErrors || $[82] !== handleValidationFailed || $[83] !== handleValidationSuccess || $[84] !== navigate || $[85] !== paymentBehaviour || $[86] !== reservationInProgress) {
        t30 = (data_0) => {
            if (reservationInProgress) {
                const { success, message, formValidation: formValidation_0, reservation } = data_0.BookingSubmit;
                if (success) {
                    pushGTMDataLayer({ transaction_id: reservation?.identifier });
                    setIsContactFormDisabled(true);
                    if (paymentBehaviour === BPaymentBehaviourEnum.Checkout) {
                        if (reservation?.clientSecret) {
                            setClientSecret(reservation.clientSecret);
                            handleStripeConfirm(reservation.clientSecret);
                        }
                        else {
                            safeWarn("submitBooking response missing clientSecret", data_0);
                            handleValidationFailed(new Error(FValidationErrorTypeEnum.MISSING_CLIENT_SECRET, { cause: data_0 }));
                        }
                    }
                    else {
                        setSubmittingBooking(false);
                        handleValidationSuccess();
                    }
                }
                else {
                    if (message === FValidationErrorTypeEnum.INVALID_FORM_ERROR_MSG) {
                        setSubmittingBooking(false);
                        safeWarn("bookingForm has errors", formValidation_0);
                        handleSubmittedContactFormErrors(formValidation_0);
                    }
                    else {
                        setSubmittingBooking(false);
                        handleValidationFailed(new Error(message ?? FValidationErrorTypeEnum.UNKNOWN_ERROR, { cause: data_0 }));
                    }
                }
            }
            else {
                alert("Error: No reservation in progress");
                setSubmittingBooking(false);
                navigate(bookingPath);
            }
        };
        $[79] = bookingPath;
        $[80] = handleStripeConfirm;
        $[81] = handleSubmittedContactFormErrors;
        $[82] = handleValidationFailed;
        $[83] = handleValidationSuccess;
        $[84] = navigate;
        $[85] = paymentBehaviour;
        $[86] = reservationInProgress;
        $[87] = t30;
    }
    else {
        t30 = $[87];
    }
    let t31;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
        t31 = (err_0) => {
            safeWarn("booking creation failed", err_0);
            setSubmittingBooking(false);
        };
        $[88] = t31;
    }
    else {
        t31 = $[88];
    }
    let t32;
    if ($[89] !== t30) {
        t32 = { onCompleted: t30, onError: t31 };
        $[89] = t30;
        $[90] = t32;
    }
    else {
        t32 = $[90];
    }
    const [submitBooking] = useMutation(BookingSubmitMutation, t32);
    const validatingCart = submittingBooking || checkingBookingStatus || enRouteToConfirmation;
    let t33;
    let t34;
    if ($[91] !== reservationInProgress) {
        t33 = () => {
            if (!reservationInProgress) {
                resetCalculatedPrice();
            }
        };
        t34 = [reservationInProgress, resetCalculatedPrice];
        $[91] = reservationInProgress;
        $[92] = t33;
        $[93] = t34;
    }
    else {
        t33 = $[92];
        t34 = $[93];
    }
    useEffect(t33, t34);
    const loadingCart = loadingCalculatedPrice || validatingCart;
    let t35;
    let t36;
    if ($[94] !== isInTunnel) {
        t35 = () => {
            if (!isInTunnel) {
                resetCart();
            }
        };
        t36 = [isInTunnel, resetCart];
        $[94] = isInTunnel;
        $[95] = t35;
        $[96] = t36;
    }
    else {
        t35 = $[95];
        t36 = $[96];
    }
    useEffect(t35, t36);
    let t37;
    let t38;
    if ($[97] !== isOnConfirmationPage || $[98] !== isOnValidationPage) {
        t37 = () => {
            if (!isOnValidationPage && !isOnConfirmationPage) {
                handleResetValidation();
            }
        };
        t38 = [isOnValidationPage, isOnConfirmationPage, handleResetValidation];
        $[97] = isOnConfirmationPage;
        $[98] = isOnValidationPage;
        $[99] = t37;
        $[100] = t38;
    }
    else {
        t37 = $[99];
        t38 = $[100];
    }
    useEffect(t37, t38);
    let t39;
    let t40;
    if ($[101] !== bookingPath || $[102] !== isOnValidationPage || $[103] !== navigate || $[104] !== reservationInProgress) {
        t39 = () => {
            if (isOnValidationPage && !reservationInProgress) {
                navigate(bookingPath);
            }
        };
        t40 = [isOnValidationPage, reservationInProgress, bookingPath, navigate];
        $[101] = bookingPath;
        $[102] = isOnValidationPage;
        $[103] = navigate;
        $[104] = reservationInProgress;
        $[105] = t39;
        $[106] = t40;
    }
    else {
        t39 = $[105];
        t40 = $[106];
    }
    useEffect(t39, t40);
    let t41;
    let t42;
    if ($[107] !== isOnConfirmationPage) {
        t41 = () => {
            if (isOnConfirmationPage) {
                resetContactForm();
                setEnRouteToConfirmation(false);
            }
        };
        t42 = [resetContactForm, isOnConfirmationPage];
        $[107] = isOnConfirmationPage;
        $[108] = t41;
        $[109] = t42;
    }
    else {
        t41 = $[108];
        t42 = $[109];
    }
    useEffect(t41, t42);
    let t43;
    if ($[110] !== handleSaveFilters || $[111] !== navigate || $[112] !== validationPath) {
        t43 = (addReservationInput) => {
            setReservationInProgress(addReservationInput);
            handleSaveFilters();
            navigate(validationPath);
        };
        $[110] = handleSaveFilters;
        $[111] = navigate;
        $[112] = validationPath;
        $[113] = t43;
    }
    else {
        t43 = $[113];
    }
    const addToCart = t43;
    let t44;
    if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
        t44 = (discountCode_0) => {
            resetDiscount(discountCode_0);
        };
        $[114] = t44;
    }
    else {
        t44 = $[114];
    }
    const applyDiscount = t44;
    let t45;
    if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
        t45 = () => {
            resetDiscount();
        };
        $[115] = t45;
    }
    else {
        t45 = $[115];
    }
    const removeDiscount = t45;
    let t46;
    if ($[116] !== params || $[117] !== submitBooking || $[118] !== submittingBooking || $[119] !== token) {
        t46 = () => {
            if (params != null && !submittingBooking) {
                setUsedParams(params);
                setSubmittingBooking(true);
                setExtra("validation.params", JSON.stringify(params));
                submitBooking({ variables: { ...params, token } });
            }
        };
        $[116] = params;
        $[117] = submitBooking;
        $[118] = submittingBooking;
        $[119] = token;
        $[120] = t46;
    }
    else {
        t46 = $[120];
    }
    const handleBook = t46;
    let t47;
    if ($[121] !== billingName || $[122] !== bookingId || $[123] !== clientSecret || $[124] !== handleBook || $[125] !== handleStripeConfirm || $[126] !== token) {
        t47 = token != null && clientSecret != null && bookingId != null && billingName != null ? () => {
            setIsContactFormDisabled(true);
            handleStripeConfirm(clientSecret);
        } :
            handleBook;
        $[121] = billingName;
        $[122] = bookingId;
        $[123] = clientSecret;
        $[124] = handleBook;
        $[125] = handleStripeConfirm;
        $[126] = token;
        $[127] = t47;
    }
    else {
        t47 = $[127];
    }
    const validateCart = t47;
    const hasCheckout = paymentBehaviour === BPaymentBehaviourEnum.Checkout;
    let t48;
    if ($[128] !== hasCheckout || $[129] !== isOnValidationPage || $[130] !== translate) {
        t48 = isOnValidationPage ?
            translate("messages.confirm_and_pay", { hasCheckout }) :
            translate("messages.finalize");
        $[128] = hasCheckout;
        $[129] = isOnValidationPage;
        $[130] = translate;
        $[131] = t48;
    }
    else {
        t48 = $[131];
    }
    const mainButtonLabel = t48;
    let t49;
    if ($[132] !== translate) {
        t49 = translate("messages.total_price");
        $[132] = translate;
        $[133] = t49;
    }
    else {
        t49 = $[133];
    }
    const totalPriceLabel = t49;
    const t50 = totalPrice || 0;
    let t51;
    if ($[134] !== currencySymbol || $[135] !== t50 || $[136] !== translate) {
        t51 = capitalize(translate("practical.price_range.unique", { price: formatPrice(t50), currencySymbol }));
        $[134] = currencySymbol;
        $[135] = t50;
        $[136] = translate;
        $[137] = t51;
    }
    else {
        t51 = $[137];
    }
    const totalPriceText = t51;
    let t52;
    if ($[138] !== translate) {
        t52 = translate("messages.total_price_before_discount");
        $[138] = translate;
        $[139] = t52;
    }
    else {
        t52 = $[139];
    }
    const totalPriceBeforeDiscountLabel = t52;
    let t53;
    if ($[140] !== currencySymbol || $[141] !== totalPrice || $[142] !== totalPriceBeforeDiscount || $[143] !== translate) {
        t53 = totalPriceBeforeDiscount && totalPriceBeforeDiscount !== totalPrice ?
            capitalize(translate("practical.price_range.unique", { price: formatPrice(totalPriceBeforeDiscount), currencySymbol })) : null;
        $[140] = currencySymbol;
        $[141] = totalPrice;
        $[142] = totalPriceBeforeDiscount;
        $[143] = translate;
        $[144] = t53;
    }
    else {
        t53 = $[144];
    }
    const totalPriceBeforeDiscountText = t53;
    let t54;
    if ($[145] !== addToCart || $[146] !== calculatedPrice || $[147] !== conversionAmount || $[148] !== creditCardError || $[149] !== discount || $[150] !== discountCodeError || $[151] !== email || $[152] !== emailError || $[153] !== firstName || $[154] !== firstNameError || $[155] !== isContactFormDisabled || $[156] !== lastName || $[157] !== lastNameError || $[158] !== loadingCart || $[159] !== mainButtonLabel || $[160] !== optInEstablishments || $[161] !== paymentBehaviour || $[162] !== phoneCountry || $[163] !== phoneError || $[164] !== phoneNumber || $[165] !== reservationInProgress || $[166] !== setIsCreditCardInError || $[167] !== setIsEmailInError || $[168] !== setIsFirstNameInError || $[169] !== setIsLastNameInError || $[170] !== setIsPhoneInError || $[171] !== specialRequest || $[172] !== totalPrice || $[173] !== totalPriceBeforeDiscountLabel || $[174] !== totalPriceBeforeDiscountText || $[175] !== totalPriceLabel || $[176] !== totalPriceText || $[177] !== validateCart) {
        t54 = { addToCart, reservationInProgress, setReservationInProgress, loadingCart, applyDiscount, removeDiscount, discountCodeError, resetDiscountCodeError, specialRequest, setSpecialRequest, isContactFormDisabled, firstName, lastName, email, phoneNumber, phoneCountry, optInEstablishments, setFirstName, setLastName, setEmail, setPhoneNumber, setPhoneCountry, setOptInEstablishments, firstNameError, lastNameError, emailError, phoneError, creditCardError, setIsFirstNameInError, setIsLastNameInError, setIsEmailInError, setIsPhoneInError, setIsCreditCardInError, validateCart, mainButtonLabel, calculatedPrice, paymentBehaviour, discount, totalPrice, conversionAmount, totalPriceLabel, totalPriceText, totalPriceBeforeDiscountLabel, totalPriceBeforeDiscountText };
        $[145] = addToCart;
        $[146] = calculatedPrice;
        $[147] = conversionAmount;
        $[148] = creditCardError;
        $[149] = discount;
        $[150] = discountCodeError;
        $[151] = email;
        $[152] = emailError;
        $[153] = firstName;
        $[154] = firstNameError;
        $[155] = isContactFormDisabled;
        $[156] = lastName;
        $[157] = lastNameError;
        $[158] = loadingCart;
        $[159] = mainButtonLabel;
        $[160] = optInEstablishments;
        $[161] = paymentBehaviour;
        $[162] = phoneCountry;
        $[163] = phoneError;
        $[164] = phoneNumber;
        $[165] = reservationInProgress;
        $[166] = setIsCreditCardInError;
        $[167] = setIsEmailInError;
        $[168] = setIsFirstNameInError;
        $[169] = setIsLastNameInError;
        $[170] = setIsPhoneInError;
        $[171] = specialRequest;
        $[172] = totalPrice;
        $[173] = totalPriceBeforeDiscountLabel;
        $[174] = totalPriceBeforeDiscountText;
        $[175] = totalPriceLabel;
        $[176] = totalPriceText;
        $[177] = validateCart;
        $[178] = t54;
    }
    else {
        t54 = $[178];
    }
    const contextValue = t54;
    let t55;
    if ($[179] !== children || $[180] !== contextValue) {
        t55 = <CartContext value={contextValue}>{children}</CartContext>;
        $[179] = children;
        $[180] = contextValue;
        $[181] = t55;
    }
    else {
        t55 = $[181];
    }
    return t55;
};
function _temp(t0) { const { key, value } = t0; return [key.replace("{{ ", "").replace(" }}", ""), value]; }
function _temp2(t0) { const { key: key_0, value: value_0 } = t0; return [key_0.replace("{{ ", "").replace(" }}", ""), value_0]; }
function _temp3(t0) { const { key: key_1, value: value_1 } = t0; return [key_1.replace("{{ ", "").replace(" }}", ""), value_1]; }
function _temp4(t0) { const { key: key_2, value: value_2 } = t0; return [key_2.replace("{{ ", "").replace(" }}", ""), value_2]; }
